import { Box, Paper, Typography } from '@mui/material';
import { isDynamicLoadError } from '@pn/services/loggers/AppSignalErrorBoundary';
import type { ErrorPageProps } from '@pn/services/loggers/AppSignalErrorBoundaryProvider';
import { CustomButton } from '@pn/ui/custom-components/CustomButton';
import { isNil } from 'lodash-es';
import { makeStyles } from 'tss-react/mui';
import { BackgroundImage } from './BackgroundImage';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: theme.spacing(2),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    maxWidth: 1200,
    padding: theme.spacing(4),
  },
}));

export function ErrorPage({ error }: ErrorPageProps) {
  const { classes } = useStyles();

  if (isNil(error)) return null;

  return (
    <Box className={classes.container}>
      <BackgroundImage />
      {isDynamicLoadError(error) ? (
        <Box component={Paper} variant="outlined" className={classes.wrapper}>
          <Typography variant="h4">Outdated version</Typography>
          <Typography variant="h6" sx={{ fontWeight: 300 }}>
            You are using an outdated version of the app. Please reload this
            page.
          </Typography>
          <Typography sx={(theme) => ({ color: theme.palette.error.light })}>
            <code>{error.message}</code>
          </Typography>
          <CustomButton size="large" onClick={() => window.location.reload()}>
            Reload
          </CustomButton>
        </Box>
      ) : (
        <Box component={Paper} variant="outlined" className={classes.wrapper}>
          <Typography variant="h4">An error happened</Typography>
          <Typography variant="h6" sx={{ fontWeight: 300 }}>
            Our team has been notified and is probably already working on it.
          </Typography>
          <Typography sx={(theme) => ({ color: theme.palette.error.light })}>
            <code>{error.message}</code>
          </Typography>
          <Typography
            sx={(theme) => ({
              color: theme.palette.error.light,
              whiteSpace: 'pre-line',
            })}
          >
            <code>{formatErrorStack(error.stack)}</code>
          </Typography>
          <CustomButton
            size="large"
            onClick={() => window.location.replace('/')}
          >
            Go back
          </CustomButton>
        </Box>
      )}
    </Box>
  );
}

function formatErrorStack(stack: string | undefined) {
  if (isNil(stack) || stack === '') {
    return null;
  } else {
    return stack.split('\n').slice(1, 4).concat('...').join('\n');
  }
}
